import React, { useMemo, useState } from "react";
import { Flex, HelpIndicator } from "@jsq/ds/core";
import { RiskRating } from "../../../../../graphql/supergraphEnums";
import { useDocumentRefreshEnabled } from "../../../../../hooks/featureFlags";
import { CountryFlagView } from "../../../../common/CountryFlagView";
import { provideLegalName } from "../../../../ProfileTree/utils";
import { RiskRatingBadge } from "../../../../riskRating/RiskRatingBadge";
import { RiskRatingDetailsModal } from "../../../../riskRating/RiskRatingDetailsModal";
import { amlStatusField, amlStatusGrid } from "../styles_vanilla.css";
import { PositionNode } from "../types";
import { FieldLabel } from "./FieldLabel";
import { PepItem } from "./PepItem";
import { RefreshStatus } from "./RefreshStatus";
import { ScreeningStatus } from "./ScreeningStatus";
import { riskRatingBadgeButton, riskRatingBadgeWrapper } from "./styles_vanilla.css";
import {
  getDisplayRiskRating,
  getDocumentRefreshStatusData,
  getProfileRefreshStatusData,
} from "./utils";
import type { HierarchyRootProfile } from "../types";

type Props = {
  positionNode: PositionNode | undefined | null;
  rootProfile: HierarchyRootProfile | undefined | null;
};

export const AmlKycStatusCard = ({ positionNode, rootProfile }: Props) => {
  const [showRiskRatingDetails, setShowRiskRatingDetails] = useState(false);

  const documentRefreshEnabled = useDocumentRefreshEnabled();

  const refreshStatusData = useMemo(() => {
    return {
      profileRefreshStatus: getProfileRefreshStatusData(positionNode?.profileRefresh),
      // Document refresh ships after the compliance dashboard. Until the flag is
      // enabled the backend does not return this data, so surface "No issues".
      documentRefreshStatus: documentRefreshEnabled
        ? getDocumentRefreshStatusData(positionNode?.documentRefresh)
        : { hasIssue: false },
    };
  }, [positionNode, documentRefreshEnabled]);

  const riskRatingContent = useMemo(() => {
    return getDisplayRiskRating(
      positionNode?.programId as number,
      positionNode?.riskRating?.positionRiskRating,
    );
  }, [positionNode?.programId, positionNode?.riskRating?.positionRiskRating]);

  const canInspectRiskRating = riskRatingContent.rating !== RiskRating.None;

  const positionName = useMemo(() => provideLegalName(rootProfile), [rootProfile]);

  return (
    <div className={amlStatusGrid}>
      <div className={amlStatusField}>
        <FieldLabel label="Domicile" />
        <CountryFlagView countryName={positionNode?.domicile?.name ?? undefined} />
      </div>

      <div className={amlStatusField}>
        <FieldLabel label="Risk rating" />
        <HelpIndicator id="position-risk-rating-tooltip" content={riskRatingContent.tooltip}>
          <Flex className={riskRatingBadgeWrapper}>
            {canInspectRiskRating ? (
              <button
                type="button"
                className={riskRatingBadgeButton}
                onClick={() => setShowRiskRatingDetails(true)}
                data-testid="position-risk-rating-inspect"
              >
                <RiskRatingBadge rating={riskRatingContent.rating} />
              </button>
            ) : (
              <RiskRatingBadge rating={riskRatingContent.rating} />
            )}
          </Flex>
        </HelpIndicator>
      </div>

      <PepItem pepCategory={positionNode?.pepExposure} />

      <ScreeningStatus screeningStatus={positionNode?.screeningStatus} />

      <RefreshStatus
        label="Profile refresh status"
        refreshStatusData={refreshStatusData.profileRefreshStatus}
      />

      <RefreshStatus
        label="Document refresh status"
        refreshStatusData={refreshStatusData.documentRefreshStatus}
      />

      <RiskRatingDetailsModal
        show={showRiskRatingDetails}
        onClose={() => setShowRiskRatingDetails(false)}
        ratings={{
          bankAccountDomicile: positionNode?.riskRating?.paymentAccountRiskRating,
          paymentBeneficiary: positionNode?.riskRating?.paymentBeneficiaryRiskRating,
          investor: positionNode?.riskRating?.investorRiskRating,
          position: positionNode?.riskRating?.positionRiskRating,
        }}
        positionName={positionName}
        entityName={positionNode?.entityName}
      />
    </div>
  );
};
